import React, { useMemo } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
} from '@mui/material';
import { SpecialSituation, SpecialSituationsService } from 'us-web-services';
import { Table, TableInstance } from 'src/components/common/Table';
import PageStyles from '../../styles/PageStyles';
import DateUtil from '../../util/DateUtil';

function SpecialSituationsLookup(props: RouteComponentProps) {
  const { history } = props;
  const [showConfirmDisable, setShowConfirmDisable] = React.useState(false);
  const [showConfirmEnable, setShowConfirmEnable] = React.useState(false);
  const [currentRow, setCurrentRow] = React.useState<SpecialSituation>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const tableRef = React.useRef<TableInstance>();

  const { classes } = PageStyles();

  const params = (globalFilter: string) => {
    const paramArray = [];

    if (globalFilter) {
      const encodedFilter = globalFilter.replace("'", "\\'");

      if (!+globalFilter) {
        const q = `name.contains('${encodedFilter}')`;

        paramArray.push({
          key: 'q',
          value: q,
        });
      } else {
        paramArray.push({
          key: 'id',
          value: globalFilter,
        });
      }
    }

    return paramArray;
  };

  const closeConfirmDisableDialog = () => {
    setShowConfirmDisable(false);
    setCurrentRow(undefined);
  };

  const closeConfirmEnableDialog = () => {
    setShowConfirmEnable(false);
    setCurrentRow(undefined);
  };

  const refreshData = () => tableRef.current?.fetchData();

  const disableItem = async () => {
    if (!currentRow?.id) return;

    setIsSubmitting(true);

    const patchData: SpecialSituation = {
      id: currentRow.id,
      name: currentRow.name,
      disabled: DateUtil.getCurrentDateTimeString(),
    };

    try {
      await SpecialSituationsService.patch(currentRow.id, patchData);
      closeConfirmDisableDialog();
      setIsSubmitting(false);
      refreshData();
      toast.success('Special situation disabled successfully');
    } catch {
      setIsSubmitting(false);
      toast.error('Error disabling special situation');
    }
  };

  const enableItem = async () => {
    if (!currentRow?.id) return;

    setIsSubmitting(true);

    const patchData: SpecialSituation = {
      id: currentRow.id,
      name: currentRow.name,
      disabled: null,
    };

    try {
      await SpecialSituationsService.patch(currentRow.id, patchData);
      closeConfirmEnableDialog();
      setIsSubmitting(false);
      refreshData();
      toast.success('Special situation enabled successfully');
    } catch {
      setIsSubmitting(false);
      toast.error('Error enabling special situation');
    }
  };

  const editItem = async (item) => {
    if (item.values.name === item.row.original.name) {
      return;
    }

    const data: SpecialSituation = {
      id: item.row.original.id,
      name: item.values.name,
      disabled: item.row.original.disabled,
    };

    try {
      await SpecialSituationsService.patch(item.row.original.id, data);
      refreshData();
      toast.success('Special situation updated successfully');
    } catch {
      toast.error('Error updating special situation');
      refreshData();
    }
  };

  const confirmDisable = (rowData) => {
    setShowConfirmDisable(true);
    setCurrentRow(rowData);
  };

  const confirmEnable = (rowData) => {
    setShowConfirmEnable(true);
    setCurrentRow(rowData);
  };

  const addItem = () => {
    history.push('/special-situations/add');
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'specialSituations',
        header: 'Special Situations',
        columns: [
          {
            header: 'Status',
            enableEditing: false,
            size: 100,
            accessorFn: row => row?.disabled ? 'Disabled' : 'Enabled',
            Cell: ({ cell }) => (
              <>
                {!!cell?.row?.original.disabled &&
                  <Tooltip title='Enable special situation' placement='top-start'>
                    <Button
                      size='small'
                      variant='contained'
                      color='error'
                      onClick={() => confirmEnable(cell?.row?.original)}
                    >
                      Disabled
                    </Button>
                  </Tooltip>
                }
                {!cell?.row?.original.disabled &&
                  <Tooltip title='Disable special situation' placement='top-start'>
                    <Button
                      size='small'
                      variant='contained'
                      color='success'
                      onClick={() => confirmDisable(cell?.row?.original)}
                    >
                      Enabled
                    </Button>
                  </Tooltip>
                }
              </>
            ),
          },
          {
            accessorKey: 'id',
            header: 'ID',
            enableEditing: false,
            size: 60,
          },
          {
            accessorKey: 'name',
            header: 'Name',
          },
        ],
      },
    ],
    [],
  );

  return (
    <Paper className={classes.paper}>
      <Dialog open={showConfirmDisable} onClose={closeConfirmDisableDialog}>
        <DialogTitle>Confirm Special Situation Deactivation</DialogTitle>
        <DialogContent>
          Are you sure you want to disable this special situation?
          It will no longer appear in the product but will be kept for record purposes.
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDisableDialog} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={disableItem}
            disabled={isSubmitting}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showConfirmEnable} onClose={closeConfirmEnableDialog}>
        <DialogTitle>Confirm Special Situation Activation</DialogTitle>
        <DialogContent>
          Are you sure you want to enable this special situation?
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmEnableDialog} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={enableItem}
            disabled={isSubmitting}
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Table
        ref={tableRef}
        columns={COLUMNS}
        retrieveData={SpecialSituationsService.getByFilterForAdmin}
        params={params}
        addAction={addItem}
        editAction={editItem}
        enableInlineEditing
        alwaysApplyParams
      />
    </Paper>
  );
}

export default withRouter(SpecialSituationsLookup);
